// 4. 10. 2026 (package 3, J2): the Relic Forge rolls three Legendary candidates and the player keeps one. The player
// picks three slots (the same slot may come more than once; a weapon slot names its class), pays the Relic Shards,
// and the server rolls one different Legendary for each slot, preferring the ones the Collection book does not have
// yet. The candidates are whole items, rolled on the survivor's tier with INT's lowest roll (`craftedItem`), and
// saved: the preview is the item itself, and closing or reloading the screen never changes them. Choosing one puts
// it on the bench as a Relic Forge job (the time and XP as before); cancelling that job brings the three back.
// No "found it in Epic" rule any more: every one of the 80 items (94 since BACKLOG #36) can come up, as long as the survivor can wear it
// (its own tier at most the survivor's; from level 16 all 80). The first forge of a season after a season reward
// (Relic Discount, part C) costs 40 instead of 60.

import { collectionKey, type CollectionState } from '../collection';
import { itemTemplate } from '../content/items';
import type { Rng } from '../rng';
import type { ItemInstance } from '../types';
import { craftedItem } from './crafted';
import { MVP_ITEMS } from './mvpItems';
import { RELIC_RULES, relicAttrWeights, relicRecipe } from './relics';
import { wearableTier } from './upgrade';

export const RELIC_SLOTS = ['weapon:melee_1h', 'weapon:melee_2h', 'weapon:ranged_1h', 'weapon:ranged_2h', 'helmet', 'body', 'gloves', 'pants', 'boots', 'backpack', 'accessory'] as const;
export type RelicSlot = (typeof RELIC_SLOTS)[number];

export const RELIC_FORGE = {
  /** False (simulations, `useRulesBefore4Oct`): the forge of package 2 (a recipe per item found in Epic). */
  on: true,
  /** Slots (and so candidates) a forge rolls. */
  picks: 3,
  /** Relic Discount (part C): the first forge of the season. */
  discountCost: 40,
};

/**
 * The survivor's forge while it waits (survivors.relic_forge): the slots, the three rolled candidates, the price paid,
 * and once one is chosen the item and the job that makes it. Cleared when the job ends.
 */
export interface RelicForgeState {
  id: string;
  slots: RelicSlot[];
  items: ItemInstance[];
  level: number;
  cost: number;
  at: number;
  picked?: string | null;
  jobId?: string | null;
}

/** Every item the forge can make (the 80 of the Collection book, no locked slot). */
export const FORGEABLE: string[] = MVP_ITEMS.map((i) => i.id).filter((id) => relicRecipe(id) !== null);

export function relicSlotOf(templateId: string): RelicSlot {
  const t = itemTemplate(templateId);
  return (t.slot === 'weapon' ? `weapon:${t.weaponClass}` : t.slot) as RelicSlot;
}

/** The slot's name on the screen: "Weapon · Ranged 2H", "Body". */
export function relicSlotName(slot: RelicSlot): string {
  if (slot.startsWith('weapon:')) {
    const c = slot.slice('weapon:'.length);
    return `Weapon · ${c.startsWith('melee') ? 'Melee' : 'Ranged'} ${c.endsWith('1h') ? '1H' : '2H'}`;
  }
  const names: Record<string, string> = { helmet: 'Head', body: 'Body', gloves: 'Hands', pants: 'Legs', boots: 'Feet', backpack: 'Pack', accessory: 'Accessory' };
  return names[slot] ?? slot;
}

/** The items a slot can roll for a survivor of `level`: the ones it can wear (from level 16 every one). */
export function relicPool(slot: RelicSlot, level: number): string[] {
  const w = wearableTier(level);
  return FORGEABLE.filter((id) => relicSlotOf(id) === slot && itemTemplate(id).tier <= w);
}

/** What a forge costs: 60 Relic Shards, 40 with the Relic Discount. */
export function relicForgeCost(discount: boolean): number {
  return discount ? RELIC_FORGE.discountCost : RELIC_RULES.forgeCost;
}

/** Why these slots cannot be rolled (three different items are needed), or null. */
export function relicSlotsProblem(slots: readonly string[], level: number): string | null {
  if (slots.length !== RELIC_FORGE.picks) return `Pick ${RELIC_FORGE.picks} slots.`;
  const count = new Map<RelicSlot, number>();
  for (const s of slots) {
    if (!(RELIC_SLOTS as readonly string[]).includes(s)) return 'Pick a slot for every candidate.';
    count.set(s as RelicSlot, (count.get(s as RelicSlot) ?? 0) + 1);
  }
  for (const [s, n] of count) {
    const have = relicPool(s, level).length;
    if (n > have) return `Only ${have} Legendary item${have === 1 ? '' : 's'} for ${relicSlotName(s)} on your tier.`;
  }
  return null;
}

const hasLegendary = (c: CollectionState | null | undefined, id: string) => !!c?.found.includes(collectionKey(id, 'legendary') ?? '');

/**
 * The three candidates: one different Legendary per slot, in the slots' order. In each slot the ones missing from the
 * Collection book come first (when there are any, the pick is among them only). Whole items on the survivor's tier.
 */
export function rollRelicCandidates(rng: Rng, o: { slots: readonly RelicSlot[]; level: number; collection: CollectionState | null | undefined; minRoll: number }): ItemInstance[] {
  const taken = new Set<string>();
  return o.slots.map((slot) => {
    const pool = relicPool(slot, o.level).filter((id) => !taken.has(id));
    if (!pool.length) throw new Error(`no Legendary left for ${slot}`);
    const missing = pool.filter((id) => !hasLegendary(o.collection, id));
    const id = rng.pick(missing.length ? missing : pool);
    taken.add(id);
    return craftedItem(rng, { templateId: id, rarity: 'legendary', level: o.level, minRoll: o.minRoll, attrWeights: relicAttrWeights(id) });
  });
}
